/**
 * Shared Password Validation
 * Reusable password validation pattern used across features
 */

import { z } from "zod";

/**
 * Password requirements for validation UI
 * Each requirement has a test function and display message
 */
export const passwordRequirements = [
  {
    id: "length",
    test: (pwd: string) => pwd.length >= 8,
    message: "At least 8 characters",
  },
  {
    id: "uppercase",
    test: (pwd: string) => /[A-Z]/.test(pwd),
    message: "Contains uppercase letter",
  },
  {
    id: "lowercase",
    test: (pwd: string) => /[a-z]/.test(pwd),
    message: "Contains lowercase letter",
  },
  {
    id: "number",
    test: (pwd: string) => /\d/.test(pwd),
    message: "Contains number",
  },
] as const;

/**
 * Check all password requirements
 * Returns array of failed requirement IDs
 */
export const getFailedPasswordRequirements = (password: string): string[] =>
  passwordRequirements
    .filter((req) => !req.test(password))
    .map((req) => req.id);

/**
 * Check if password meets all requirements
 */
export const isStrongPassword = (password: string): boolean =>
  passwordRequirements.every((req) => req.test(password));

/**
 * Zod schema for strong password (signup)
 * Enforces all password requirements
 */
export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain an uppercase letter")
  .regex(/[a-z]/, "Password must contain a lowercase letter")
  .regex(/\d/, "Password must contain a number");

/**
 * Zod schema for login password (less strict)
 * Only requires non-empty - actual validation happens on server
 */
export const loginPasswordSchema = z.string().min(1, "Password is required");

/**
 * Zod schema for confirm password
 * Used with .refine() on parent object to check match
 */
export const confirmPasswordSchema = z
  .string()
  .min(1, "Please confirm your password");
